import { useEffect, useState } from 'react';
import { getLegalPages, LegalPagesResponse } from '@/lib/collections/legalPages';
import { PageLayout } from '@/components/poof-ui';
import { Link } from 'react-router-dom';

const DEFAULT_PRIVACY = `PRIVACY POLICY

Last updated by administrator.

1. DATA WE COLLECT
- Wallet address (when you connect)
- Twitter/X username (when you link your account)

2. HOW WE USE IT
- To run the lucky draw giveaway
- To contact winners
- We do not sell your data

3. COOKIES
We use minimal cookies for session management only.

4. CONTACT
For privacy concerns, contact us on Twitter/X @dangerdan_nft`;

function formatUpdatedAt(ts: number): string {
  const d = new Date(ts * 1000);
  const datePart = d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
  const timePart = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
  return `${datePart} at ${timePart} UTC`;
}

export default function PrivacyPage() {
  // undefined = still loading, null = not found, LegalPagesResponse = loaded
  const [page, setPage] = useState<LegalPagesResponse | null | undefined>(undefined);

  useEffect(() => {
    getLegalPages('privacy').then(setPage);
  }, []);

  const isLoading = page === undefined;
  const dbContent = page?.content ?? null;
  const updatedAt = page?.updatedAt ?? null;
  const displayContent = dbContent !== null ? dbContent : DEFAULT_PRIVACY;

  return (
    <PageLayout fullBleed>
      {/* Background halftone texture */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          background: '#080d1f',
          backgroundImage: `radial-gradient(circle, rgba(124,252,0,0.04) 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh', padding: '3rem 1.5rem 5rem', background: '#080d1f' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>

          {/* Header */}
          <div className="mb-8">
            <div
              style={{
                display: 'inline-block',
                background: '#7CFC00',
                color: '#080d1f',
                fontFamily: "'Bangers', cursive",
                letterSpacing: '0.18em',
                fontSize: '0.7rem',
                padding: '2px 10px',
                marginBottom: '1rem',
                textTransform: 'uppercase',
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)',
              }}
            >
              Legal
            </div>

            <h1
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: 'clamp(2.5rem, 7vw, 4rem)',
                letterSpacing: '0.06em',
                color: '#7CFC00',
                textShadow: '3px 3px 0 rgba(124,252,0,0.12), 0 0 40px rgba(124,252,0,0.18)',
                lineHeight: 1.05,
                marginBottom: '0.5rem',
              }}
            >
              Privacy Policy
            </h1>

            {updatedAt !== null && (
              <p
                style={{
                  fontFamily: 'monospace',
                  fontSize: '0.78rem',
                  color: 'rgba(124,252,0,0.55)',
                  marginBottom: 0,
                }}
              >
                Last Updated: {formatUpdatedAt(updatedAt)}
              </p>
            )}
          </div>

          {/* Content panel */}
          <div
            style={{
              background: 'rgba(10,16,35,0.85)',
              border: '3px solid #7CFC00',
              borderRadius: '0.25rem',
              padding: '2rem 2rem 2.5rem',
              boxShadow: '4px 4px 0 rgba(124,252,0,0.22), inset 0 0 60px rgba(0,0,0,0.4)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Corner accent */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '0 28px 28px 0',
                borderColor: 'transparent #7CFC00 transparent transparent',
              }}
            />

            {isLoading ? (
              <p style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                Loading...
              </p>
            ) : (
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '0.92rem',
                  lineHeight: 1.75,
                  color: dbContent !== null ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.6)',
                  margin: 0,
                }}
              >
                {displayContent}
              </pre>
            )}
          </div>

          {/* Back link */}
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <Link
              to="/"
              style={{
                fontFamily: "'Bangers', cursive",
                letterSpacing: '0.12em',
                color: 'rgba(124,252,0,0.6)',
                fontSize: '0.85rem',
                textDecoration: 'none',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#7CFC00'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(124,252,0,0.6)'; }}
            >
              &larr; Back to Home
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
